// src/components/resumo/annotations/textHighlighter.tsx
// 🎯 Renderiza texto com grifos usando offsets absolutos GLOBAIS

import React from 'react';
import { Highlight } from './annotations.types';
import './HighlightCommentIcon.css';

/**
 * Processa HTML e insere grifos como elementos React
 */
export const renderHTMLWithHighlights = (
    html: string,
    highlights: Highlight[]
): React.ReactNode => {

    // ✅ ADICIONAR: Ordenar grifos por posição e remover sobreposições
    const sortedHighlights = highlights
        .sort((a, b) => a.startOffset - b.startOffset)
        .filter((highlight, index, array) => {
            // Remover grifos que se sobrepõem ao anterior
            if (index === 0) return true;

            const prev = array[index - 1];
            // Se começa antes do anterior terminar, é sobreposição
            return highlight.startOffset >= prev.endOffset;
        });

    if (!highlights || highlights.length === 0) {
        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    }

    // Criar container temporário para parser
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // ✅ Processar com offset global
    processTextNodes(tempDiv, sortedHighlights, { value: 0 });

    return <div dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }} />;
};

/**
 * ✅ Processa recursivamente nós de texto com CONTADOR GLOBAL
 */
function processTextNodes(
    node: Node,
    highlights: Highlight[],
    globalOffset: { value: number }
): void {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';

        if (!text.trim()) {
            globalOffset.value += text.length;
            return;
        }

        // ✅ Encontrar grifos que intersectam com ESTE nó
        const nodeStart = globalOffset.value;
        const nodeEnd = globalOffset.value + text.length;

        const applicableHighlights = highlights
            .map(h => {
                // ✅ Calcular interseção entre grifo global e nó local
                const startInNode = Math.max(h.startOffset - nodeStart, 0);
                const endInNode = Math.min(h.endOffset - nodeStart, text.length);

                // Só aplicar se houver interseção
                if (startInNode < endInNode && h.startOffset < nodeEnd && h.endOffset > nodeStart) {
                    return { highlight: h, start: startInNode, end: endInNode };
                }
                return null;
            })
            .filter(Boolean) as { highlight: Highlight; start: number; end: number }[];

        if (applicableHighlights.length === 0) {
            globalOffset.value += text.length;
            return;
        }

        // ✅ Criar fragmento com grifos
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        // Ordenar por posição
        applicableHighlights.sort((a, b) => a.start - b.start);

        applicableHighlights.forEach(({ highlight, start, end }) => {
            const isLastFragment =
                highlight.endOffset <= nodeEnd &&
                end === Math.min(highlight.endOffset - nodeStart, text.length);
            // Texto antes do grifo
            if (start > lastIndex) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
            }

            // Grifo
            const mark = document.createElement('mark');
            mark.className = `annotation-highlight highlight-${highlight.color} highlight-${highlight.thickness} highlight-${highlight.style}`;
            mark.dataset.highlightId = highlight.id;
            mark.dataset.color = highlight.color;
            mark.dataset.thickness = highlight.thickness;
            mark.dataset.style = highlight.style;

            // ✅ TEXTO DO GRIFO
            const textNode = document.createTextNode(text.slice(start, end));
            mark.appendChild(textNode);
            fragment.appendChild(mark);

            // ✅ ADICIONAR ÍCONE SE TIVER COMENTÁRIO (fora do mark para não deslocar offsets de texto)
            if (highlight.note && highlight.note.trim() && isLastFragment) {
                const icon = document.createElement('span');
                icon.className = 'highlight-comment-badge';
                icon.innerHTML = '💬';
                icon.dataset.comment = highlight.note;
                fragment.appendChild(icon);
            }
            lastIndex = end;
        });

        // Texto depois do último grifo
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        // Substituir nó original
        if (node.parentNode) {
            node.parentNode.replaceChild(fragment, node);
        }

        globalOffset.value += text.length;

    } else if (node.nodeType === Node.ELEMENT_NODE) {
        if ((node as HTMLElement).tagName === 'MARK') return;
        // O badge fica fora do <mark> mas ainda no DOM — não contar seus caracteres no offset
        if ((node as HTMLElement).classList?.contains('highlight-comment-badge')) return;

        Array.from(node.childNodes).forEach(child =>
            processTextNodes(child, highlights, globalOffset)
        );
    }
}